import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { createSearchParams, useNavigate, useSearchParams } from 'react-router-dom'
import Booking from '../../Components/Booking/Booking'
import { bookingType } from '../../Components/Booking/booking.schema'
import PhoneVerification from '../../Components/PhoneVerification/phone-verification'
import ResultCard from '../../Components/ResultCard/ResultCard'
import { FleetModel } from '../../models/fleet.model'
import { VehicleService } from '../../services/vehicle.service'
import { showPhoneVerificationModalHandler as showPhoneVerificationModalAction } from '../../store/profile-data.slice'
import { StoreInterface } from '../../store/store'
import style from './list.module.scss'
import { VehicleForBookingResponse } from '../../types/api.types'
import { BookingInfoModal, BookingInfoFormData } from '../../Components/BookingInfoModal'

export interface SearchParams {
  locations: string[]
  pickupDate: string
  pickupTime: string
}
const vehicleService = new VehicleService()
const List = () => {
  const { refreshToken, showPhoneVerificationModal } = useSelector(
    (store: StoreInterface) => store.profile,
  )

  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [searchParams] = useSearchParams()
  const [fleets, setFleets] = useState<FleetModel[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showBookingModal, setShowBookingModal] = useState(false)
  const [selectedFleet, setSelectedFleet] = useState<FleetModel | null>(null)

  const defaultValues = {
    tripType: searchParams.get('tripType') || 'one-way',
    locations: searchParams.getAll('locations').map(e => {
      return { value: e }
    }),
    pickupDate: searchParams.get('pickupDate'),
    pickupTime: searchParams.get('pickupTime'),
    returnDate: searchParams.get('returnDate'),
    returnTime: searchParams.get('returnTime'),
  } as bookingType

  // Fetch vehicles when component mounts or search params change
  useEffect(() => {
    const fetchVehicles = async () => {
      const locations = searchParams.getAll('locations')
      if (locations.length < 2) {
        setError('Please provide pickup and dropoff locations')
        return
      }

      setLoading(true)
      setError(null)

      try {
        const tripType = searchParams.get('tripType') as 'one-way' | 'return' | null
        const requestData = {
          pickupAddress: locations[0],
          dropoffAddress: locations[1],
          tripType: tripType || 'one-way',
        }

        const res = await vehicleService.getVehiclesForBookings(requestData)
        const fleetModels = res.data.vehicles.map(
          (vehicle: VehicleForBookingResponse) => new FleetModel(vehicle),
        )
        setFleets(fleetModels)
      } catch (err: any) {
        console.error('Error fetching vehicles:', err)
        setError('Failed to load vehicles. Please try again.')
      } finally {
        setLoading(false)
      }
    }

    fetchVehicles()
  }, [searchParams])

  const handleBookingUpdate = (data: bookingType) => {
    // Update the URL search parameters with new search criteria
    let params = new URLSearchParams()
    data.locations.forEach(location => {
      params.append('locations', location.value)
    })
    params.append('tripType', data.tripType)
    params.append('pickupDate', data.pickupDate)
    params.append('pickupTime', data.pickupTime)

    if (data.tripType === 'return' && data.returnDate && data.returnTime) {
      params.append('returnDate', data.returnDate)
      params.append('returnTime', data.returnTime)
    }

    // Navigate with new parameters - this will trigger useEffect to fetch new vehicles
    navigate({
      pathname: '/list',
      search: '?' + createSearchParams(params.toString()),
    })
  }

  const goToCheckout = (data: FleetModel) => {
    if (!refreshToken) {
      dispatch(showPhoneVerificationModalAction(true))
      return
    }

    // Store selected fleet and show modal
    setSelectedFleet(data)
    setShowBookingModal(true)
  }

  const handleBookingInfoConfirm = (bookingInfo: BookingInfoFormData) => {
    if (!selectedFleet) return

    // Prepare URL parameters
    let params = new URLSearchParams()

    // Original booking params
    searchParams.getAll('locations').forEach(e => {
      params.append('locations', e)
    })
    params.append('tripType', defaultValues.tripType)
    params.append('pickupDate', defaultValues.pickupDate)
    params.append('pickupTime', defaultValues.pickupTime)

    if (defaultValues.tripType === 'return' && defaultValues.returnDate && defaultValues.returnTime) {
      params.append('returnDate', defaultValues.returnDate)
      params.append('returnTime', defaultValues.returnTime)
    }

    params.append('fleetId', selectedFleet.id)
    params.append('totalCostAmount', selectedFleet.totalCost.amount.toString())
    params.append('totalCostCurrencyCode', selectedFleet.totalCost.currencyCode)

    // Add booking info params
    if (bookingInfo.flightNumber) params.append('flightNumber', bookingInfo.flightNumber)
    if (bookingInfo.pickupSign) params.append('pickupSign', bookingInfo.pickupSign)
    if (bookingInfo.passengerCount) params.append('passengerCount', bookingInfo.passengerCount.toString())
    if (bookingInfo.luggage) params.append('luggage', bookingInfo.luggage)
    if (bookingInfo.notes) params.append('notes', bookingInfo.notes)

    const isBookingForSomeoneElse = bookingInfo.bookingForSomeoneElse === 'someoneelse'
    params.append('bookingForSomeoneElse', isBookingForSomeoneElse.toString())

    if (isBookingForSomeoneElse) {
      if (bookingInfo.guestFullName) params.append('guestFullName', bookingInfo.guestFullName)
      if (bookingInfo.guestEmail) params.append('guestEmail', bookingInfo.guestEmail)
      if (bookingInfo.guestPhoneNumber) params.append('guestPhoneNumber', bookingInfo.guestPhoneNumber)
    }

    // Navigate to payment
    navigate({
      pathname: '/payment',
      search: '?' + createSearchParams(params.toString()),
    })
  }

  const handleBookingModalClose = () => {
    setShowBookingModal(false)
    setSelectedFleet(null)
  }

  const closePhoneVerificationModal = () => {
    dispatch(showPhoneVerificationModalAction(false))
  }

  const handlePhoneVerificationSuccess = (user: any) => {
    dispatch(showPhoneVerificationModalAction(false))
    // User is now logged in, they can proceed with checkout
  }

  return (
    <div className={style.list}>
      <div className="container">
        <div className="flex-wrap">
          <div className={style.list__form}>
            <Booking
              sidebarUI={true}
              defaultValues={defaultValues}
              buttonText="Update"
              onSubmit={handleBookingUpdate}
            />
          </div>
          <div className={style.list__right}>
            <div className={`${style.list__header} flex-wrap align-center justify-between`}>
              <aside>
                <p>{fleets.length} Cars • Total includes taxes and fees</p>
              </aside>
              <aside></aside>
            </div>

            <div className={style.results__content}>
              {loading && (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <p>Loading vehicles...</p>
                </div>
              )}
              {error && (
                <div style={{ textAlign: 'center', padding: '20px', color: 'red' }}>
                  <p>Error: {error}</p>
                </div>
              )}
              {!loading && !error && fleets.length === 0 && (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <p>No vehicles available for the selected locations.</p>
                </div>
              )}
              {!loading &&
                !error &&
                fleets.map((e: FleetModel) => {
                  return <ResultCard data={e} key={e.id} goToCheckout={() => goToCheckout(e)} />
                })}
            </div>
          </div>
        </div>
      </div>

      <PhoneVerification
        isOpen={showPhoneVerificationModal}
        onClose={closePhoneVerificationModal}
        onSuccess={handlePhoneVerificationSuccess}
      />

      <BookingInfoModal
        isOpen={showBookingModal}
        onClose={handleBookingModalClose}
        onConfirm={handleBookingInfoConfirm}
        fleetName={selectedFleet?.vehicleName}
      />
    </div>
  )
}
export default List

// Note: Loader function removed in favor of component-level data fetching
// to properly handle authentication tokens and state management
